import React, { useState, useRef, useEffect } from "react";
import { Grid, IconButton } from "@mui/material";
import { useStyles } from "../../../../utils/pdf-viewer/style";
import { BootstrapTooltip } from "../../../../utils/Tooltip";
import {
  ZoomInIcon,
  ZoomOutIcon,
  RotateClockwiseIcon,
  DownloadIcon,
  RedirectIcon
} from "../../../Icons";

interface ImageViewerProps {
  url: string;
  name?: string;
  newTabUrl?: string;
}

export const ImageViewer = ({ url, name, newTabUrl }: ImageViewerProps) => {
  const classes = useStyles();
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [naturalDims, setNaturalDims] = useState({ w: 0, h: 0 });
  const [containerW, setContainerW] = useState(0);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Track container width so we can compute exact pixel dimensions
  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerW(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleImgLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const t = e.currentTarget;
    setNaturalDims({ w: t.naturalWidth, h: t.naturalHeight });
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = url;
    link.download = name ?? "image";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNewTab = () => {
    if (newTabUrl) window.open(newTabUrl, "_blank");
  };

  // Once both natural dims and container width are known, compute exact pixel layout
  const isReady = containerW > 0 && naturalDims.w > 0;
  const isRotated = rotation % 180 !== 0;

  // Image rendered width = container width * zoom; height derived from aspect ratio
  const imgW = isReady ? containerW * zoom : 0;
  const imgH = isReady ? imgW * (naturalDims.h / naturalDims.w) : 0;

  // Visual bounding box after rotation — this drives the wrapper size so
  // overflow:auto on the scroll container sees real layout and shows scrollbars
  const wrapW = isRotated ? imgH : imgW;
  const wrapH = isRotated ? imgW : imgH;

  return (
    <Grid className={classes.pdfContainer}>
      <div className={classes.controlsBox}>
        <div style={{ padding: "0px 2px" }}>
          <BootstrapTooltip title={zoom <= 0.2 ? "" : "Zoom out"} placement="bottom" arrow>
            <span>
              <IconButton
                aria-label="zoom-out"
                onClick={() => setZoom((prev) => Math.max(0.2, Number((prev - 0.1).toFixed(2))))}
                className={`${classes.toolbarIcon} ${classes.zoomOut}`}
                disabled={zoom <= 0.2}
                size="large"
              >
                <ZoomOutIcon size="18" />
              </IconButton>
            </span>
          </BootstrapTooltip>
        </div>
        <div style={{ padding: "0px 2px", minWidth: 50, textAlign: "center", fontSize: 13 }}>
          {(zoom * 100).toFixed(0)}%
        </div>
        <div style={{ padding: "0px 2px" }}>
          <BootstrapTooltip title={zoom >= 3 ? "" : "Zoom in"} placement="bottom" arrow>
            <span>
              <IconButton
                aria-label="zoom-in"
                onClick={() => setZoom((prev) => Math.min(3, Number((prev + 0.1).toFixed(2))))}
                className={classes.toolbarIcon}
                disabled={zoom >= 3}
                size="large"
              >
                <ZoomInIcon size="18" />
              </IconButton>
            </span>
          </BootstrapTooltip>
        </div>
        <div style={{ padding: "0px 2px" }}>
          <BootstrapTooltip title="Rotate clockwise" placement="bottom" arrow>
            <IconButton
              aria-label="rotate-clockwise"
              onClick={() => setRotation((prev) => prev + 90)}
              className={classes.toolbarIcon}
              size="large"
            >
              <RotateClockwiseIcon viewBox="0 3 16 16" />
            </IconButton>
          </BootstrapTooltip>
        </div>
        <div style={{ padding: "0px 2px" }}>
          <BootstrapTooltip title="Open in new tab" placement="bottom" arrow>
            <IconButton
              aria-label="open-in-new-tab"
              onClick={handleOpenInNewTab}
              className={classes.toolbarIcon}
              size="large"
            >
              <RedirectIcon color="#183238" width={20} height={20} />
            </IconButton>
          </BootstrapTooltip>
        </div>
        <div style={{ padding: "0px 2px", marginRight: 5 }}>
          <BootstrapTooltip title="Download" placement="bottom" arrow>
            <IconButton
              aria-label="download"
              onClick={handleDownload}
              className={classes.toolbarIcon}
              size="large"
            >
              <DownloadIcon size="21" viewBox="0 1 16 16" />
            </IconButton>
          </BootstrapTooltip>
        </div>
      </div>

      <div
        ref={scrollAreaRef}
        style={{
          overflow: "auto",
          width: "100%",
          height: "100%",
          backgroundColor: "#f8f9ff"
        }}
      >
        {/*
          Wrapper is sized to the post-rotation visual bounding box in real pixels.
          This gives overflow:auto real layout dimensions to scroll against —
          CSS transforms alone don't affect layout flow.
        */}
        <div
          style={{
            width: isReady ? wrapW : "100%",
            height: isReady ? wrapH : "auto",
            position: "relative",
            flexShrink: 0
          }}
        >
          <img
            src={url}
            alt={name}
            onLoad={handleImgLoad}
            style={
              isReady
                ? {
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    width: imgW,
                    height: imgH,
                    transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                    transformOrigin: "center center",
                    transition: "transform 0.3s ease",
                    maxWidth: "none"
                  }
                : {
                    maxWidth: "100%",
                    display: "block"
                  }
            }
          />
        </div>
      </div>
    </Grid>
  );
};
